
export interface StockDataPoint {
  date: string;
  value: number;
  volume?: number;
}

export interface StockData {
  id: string;
  name: string;
  symbol: string;
  category: string;
  data: StockDataPoint[];
  prediction?: StockDataPoint[];
  changePercent: number;
  changeValue: number;
  currentValue: number;
}

// Helper function to generate realistic-looking stock data
const generateStockData = (
  baseValue: number,
  volatility: number,
  days: number,
  startDate: Date = new Date(2023, 0, 1)
): StockDataPoint[] => {
  const data: StockDataPoint[] = [];
  let currentValue = baseValue;

  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    
    // Skip weekends
    if (date.getDay() === 0 || date.getDay() === 6) {
      continue;
    }
    
    // Random walk with slight upward bias
    const change = (Math.random() - 0.48) * volatility * currentValue;
    currentValue += change;
    
    // Ensure no negative values
    if (currentValue < 0.1 * baseValue) {
      currentValue = 0.1 * baseValue;
    }
    
    // Generate volume - higher on bigger price changes
    const volume = Math.round(
      baseValue * 100000 * (1 + Math.abs(change / currentValue) * 10) * (0.5 + Math.random())
    );
    
    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(currentValue * 100) / 100,
      volume
    });
  }
  
  return data;
};

// Generate prediction data extending from the last real data point
const generatePrediction = (lastDataPoint: StockDataPoint, days: number): StockDataPoint[] => {
  const predictions: StockDataPoint[] = [];
  let currentValue = lastDataPoint.value;
  const lastDate = new Date(lastDataPoint.date);
  
  // Slightly upward biased prediction
  for (let i = 1; i <= days; i++) {
    const date = new Date(lastDate);
    date.setDate(date.getDate() + i);
    
    // Skip weekends for realism
    if (date.getDay() === 0 || date.getDay() === 6) {
      continue;
    }
    
    // More conservative changes for predictions
    const change = (Math.random() - 0.45) * 0.01 * currentValue;
    currentValue += change;
    
    predictions.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(currentValue * 100) / 100,
    });
  }
  
  return predictions;
};

// Generate sample stock data
const createStockDataset = (
  id: string,
  name: string,
  symbol: string,
  category: string,
  baseValue: number,
  volatility: number
): StockData => {
  const data = generateStockData(baseValue, volatility, 180);
  const lastDataPoint = data[data.length - 1];
  const prediction = generatePrediction(lastDataPoint, 30);
  
  const firstValue = data[0].value;
  const currentValue = lastDataPoint.value;
  const changeValue = currentValue - firstValue;
  const changePercent = (changeValue / firstValue) * 100;
  
  return {
    id,
    name,
    symbol,
    category,
    data,
    prediction,
    changePercent: Math.round(changePercent * 100) / 100,
    changeValue: Math.round(changeValue * 100) / 100,
    currentValue
  };
};

// Sample datasets
export const stockDatasets: StockData[] = [
  createStockDataset('sp500', 'S&P 500', 'SPX', 'Index', 4500, 0.01),
  createStockDataset('nasdaq', 'NASDAQ Composite', 'IXIC', 'Index', 15000, 0.012),
  createStockDataset('djia', 'Dow Jones Industrial Average', 'DJI', 'Index', 37000, 0.009),
  createStockDataset('apple', 'Apple Inc', 'AAPL', 'Stock', 180, 0.015),
  createStockDataset('microsoft', 'Microsoft Corporation', 'MSFT', 'Stock', 340, 0.011),
  createStockDataset('amazon', 'Amazon.com Inc', 'AMZN', 'Stock', 140, 0.016),
  createStockDataset('google', 'Alphabet Inc', 'GOOGL', 'Stock', 130, 0.013),
  createStockDataset('tesla', 'Tesla Inc', 'TSLA', 'Stock', 240, 0.025),
  createStockDataset('crude-oil', 'Crude Oil', 'CL.1', 'Commodity', 75, 0.018),
  createStockDataset('gold', 'Gold', 'GC.1', 'Commodity', 1950, 0.007),
  createStockDataset('bitcoin', 'Bitcoin', 'BTC-USD', 'Crypto', 43000, 0.025),
  createStockDataset('ethereum', 'Ethereum', 'ETH-USD', 'Crypto', 2300, 0.028),
];

export const marketIndices = stockDatasets.filter(item => item.category === 'Index');
export const topStocks = stockDatasets.filter(item => item.category === 'Stock');
export const commodities = stockDatasets.filter(item => item.category === 'Commodity');
export const crypto = stockDatasets.filter(item => item.category === 'Crypto');

export const featuredDatasets = [
  stockDatasets.find(item => item.id === 'sp500'),
  stockDatasets.find(item => item.id === 'apple'),
  stockDatasets.find(item => item.id === 'crude-oil'),
  stockDatasets.find(item => item.id === 'bitcoin'),
].filter(Boolean) as StockData[];
